import React, { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

import AuthContext from '../../context/authContext';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import useStatisticJob from '../../hooks/useStatisticJob';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_BON_STATS } from '../../constants/statisticTypes';

const KPI = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}
  >
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 600 }}>
      {value}
    </Typography>
  </Paper>
);

const BonStats = () => {
  const authCtx = useContext(AuthContext);
  if (!authCtx.checkRight('statistic:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const [startDate, setStartDate] = useState(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));

  // Seed with today's range so the view auto-loads the most recent cached
  // entry (if any) on mount instead of showing an empty page until the
  // user clicks "Auswerten".
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
  });
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    refresh,
    loadCacheEntry,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_BON_STATS,
    endpoint: '/api/v1/bon/stats',
    filterParams,
    autoFetchOnMount: true,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_BON_STATS,
  });

  const handleApply = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || e.isBefore(s, 'day')) return;
      const snapshot = {
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
      };
      setAppliedFilter(snapshot);
      setSelectedEntryId(null);
    },
    [startDate, endDate]
  );

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId || null);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const applyIsDisabled = !startDate || !endDate || endDate.isBefore(startDate, 'day');
  const refreshIsDisabled =
    status === 'fetching' || status === 'pending' || status === 'running';

  const shellFilterParamConfig = useMemo(
    () => ({
      branchScope: { hidden: true },
    }),
    []
  );

  const stats = payload;

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onApply={handleApply}
      applyDisabled={applyIsDisabled}
      applyLabel='Auswerten'
      applyInFilterRow
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refresh}
      refreshDisabled={refreshIsDisabled}
      filterParamConfig={shellFilterParamConfig}
      hasPayload={payload != null}
    >
      {stats && (
        <Fragment>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KPI label='Anzahl Bons gesamt' value={stats.totalBons} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                label='Bons ohne Standort'
                value={`${stats.withoutBranch} (${stats.withoutBranchPct}%)`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI
                label='Bons ohne Entertainment'
                value={`${stats.withoutEntertainment} (${stats.withoutEntertainmentPct}%)`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPI label='Positionen ohne Warengruppe' value={stats.positionsWithoutGroup} />
            </Grid>
          </Grid>

          <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
            <Typography variant='h6'>Aufschlüsselung nach Standort</Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Standort</TableCell>
                  <TableCell align='right'>Bons</TableCell>
                  <TableCell align='right'>Ohne Entertainment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(stats.byBranch || []).map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.branchName || '—'}</TableCell>
                    <TableCell align='right'>{row.bons}</TableCell>
                    <TableCell align='right'>{row.withoutEntertainment}</TableCell>
                  </TableRow>
                ))}
                {(stats.byBranch || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box sx={{ py: 2, textAlign: 'center' }}>
                        <Typography variant='body2' color='text.secondary'>
                          Keine Daten im gewählten Zeitraum.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Fragment>
      )}
    </StatisticViewShell>
    </Box>
  );
};

export default BonStats;
